import { useI18n } from '@/hooks/useI18n';
import { IResourcePermission, parseDateOnlyString, WeekDayNames  } from '@gettactic/api';
import {
  useAuthenticated,
  useIsDesignSystem2,
} from '@gettactic/hooks';
import { format } from 'date-fns';
import { Row } from 'react-table';
import { AnchorDaysObject } from '../basic/DatePicker/AnchorDayPicker';
import { classNames } from '@/lib/classNames';

export function PermissionsDatesCell({
  row: { original: permission }
}: { row: Row<IResourcePermission> }) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { templates } = useI18n(authenticatedUser);

  const since = permission.since ? parseDateOnlyString(permission.since) : null;
  const until = permission.until ? parseDateOnlyString(permission.until) : null;

  const hasAnchorDays = permission?.anchor_days && permission?.anchor_days.length > 0;
  const receivedDays = permission?.anchor_days || [];
  const anchorDays = receivedDays.reduce(
    (acc: AnchorDaysObject, day) => {
      acc[day as keyof AnchorDaysObject] = true;
      return acc;
    },
    {} as AnchorDaysObject
  );

  const isV2 = useIsDesignSystem2();

  return (
    <div className="flex flex-col items-center justify-between">
      <div className="flex items-center justify-start space-x-1 text-sm">
        {!since && !until ? (
          <span className="font-bold">Permanent</span>
        ) : (
          <>
            <span>
              {since ? format(since, templates.displayDateShort) + ' ' : null}
            </span>
            <span>—</span>
            {until ? (
              <div>{format(until, templates.displayDateShort)}</div>
            ) : (
              <span className="font-bold">No End</span>
            )}
          </>
        )}
      </div>
      {isV2 && hasAnchorDays ? <AnchorDays anchorDays={anchorDays} /> : null}
    </div>
  );
}

const dayOrder: WeekDayNames[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday'
];

const AnchorDays = ({ anchorDays }: { anchorDays: AnchorDaysObject }) => {
  return (
    <div className='flex items-center space-x-1 text-neutral-500 text-xs'>
      {dayOrder.map((day) => (
        <span
          key={day}
          className={classNames(
            anchorDays[day as keyof AnchorDaysObject] ? 'text-brand' : 'text-neutral-500',
            'flex items-center justify-center font-semibold'
          )}
        >
          {day.charAt(0).toUpperCase()}
        </span>
      ))}
    </div>
  );
};
